import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import mapValues from 'lodash/mapValues';
import type { Get, ConditionalPick } from 'type-fest';

import { isEmpty } from './checks';
import compareValues, { castString, Change } from './compareValues';
import type { Literal } from './guards';
import {
  isObjectDeepPath, isArrayOf, isLiteral,
} from './guards';

// eslint-disable-next-line local/prefer-nullable
export type Nullable<T> = T | null | undefined;

// for limiting recursive types' depth
type Decr = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// like PartialDeep<Obj> from type-fest but ignores things that aren't plain objects

// type Thing = {
//   a: string;
//   b: {
//     c: {
//       d: number[];
//     };
//     e: Date;
//   };
// };
//
// PartialDeep<Thing> === {
//   a?: string
//   b?: {
//     c?: {
//       d?: number[]
//     },
//     e?: Date
//   }
// }

export type SelectivePartial<Obj, Keys extends keyof Obj> = Obj extends Literal ? {
  [key in (keyof Obj & Keys)]?: Obj[key]
} & Omit<Obj, Keys> : Obj;

export type PartialDeepObject<Obj> = Obj extends Literal | Nullable<Literal> ? {
  [key in keyof Obj]?: PartialDeepObject<Obj[key]>
} : Obj;

export type NullablePartial<Obj> = Obj extends Literal ? {
  [key in keyof Obj]?: Nullable<Obj[key]>
} : Obj;

export type ReplaceObjectValues<
  Obj, ToReplace, Replacement> = Obj extends Literal | Partial<Literal>
  ? { [ key in keyof Obj]: ReplaceObjectValues<Obj[key], ToReplace, Replacement> }
  : Obj extends ToReplace
    ? Replacement
    : Obj;

export type ObjectShape<Obj, DefaultValue = unknown> = Obj extends Literal | Partial<Literal> ? {
  [key in keyof Obj]: ObjectShape<Obj[key], DefaultValue>
} : DefaultValue;

type ObjectComparisonResult<T> = T extends Literal
  ? { [key in keyof T]: ObjectComparisonResult<T[key]> }
  : Change;

// Values that are currently in inputs are usually not parsed
// meaning that even though a field is yup.number() in schema, its value is actually a string
// Same goes for yup.date() and problably some others.

type ReplaceFormValues<Obj, ToReplace, Replacement> = Obj extends Literal | Partial<Literal>
  ? { [ key in keyof Obj]: ReplaceFormValues<Obj[key], ToReplace, Replacement> }
  : Obj extends ToReplace
    ? Replacement
    : Obj extends (infer T)[]
      ? ReplaceFormValues<T, ToReplace, Replacement>[]
      : Obj;

export type FormValues<T extends Literal> = ReplaceFormValues<
  ReplaceFormValues<T, Date, Nullable<Date>>, number, number | string>;

export type GetValue<T> = T extends Record<'value', infer U>
  ? GetValue<U>
  : T extends Partial<Record<'value', infer U>>
    ? Nullable<GetValue<U>>
    : T;

type FlattenValues<Obj> = Obj extends Partial<Literal>
  ? Obj extends Partial<Record<'value', unknown>>
    ? GetValue<Obj>
    : { [key in keyof Obj]: GetValue<Obj[key]> }
  : Obj;

export type DeepRequired<T> = T extends Literal
  ? { [key in keyof T]-?: DeepRequired<T[key]> }
  : T extends null | undefined
    ? Nullable<Exclude<T, undefined>>
    : T;

// Merges proerties from ObjA and ObjB, (ObjB values override ones from ObjA).
// Plain object values are also merged recursively

type MergeObject<ObjA, ObjB> = {
  [key in keyof ObjA | keyof ObjB]: key extends keyof ObjB
    ? ObjB[key]
    : key extends keyof ObjA
      ? ObjA[key]
      : never
}

export type OmitByValue<Obj, Value> = Readonly<{
  [key in keyof Obj as Obj[key] extends Value ? never : key] : Obj[key]
}>

type DeepMakeOptional<T, P extends string> =
  P extends `${infer Key}.${infer Rest}`
    ? Key extends keyof T
      ? { [K in Key]: DeepMakeOptional<T[K], Rest> } & Omit<T, Key>
      : T
    : P extends keyof T
      ? Omit<T, P> & { [key in P]?: T[key] }
      : T;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DeepSelectivePartial<T, P extends ObjectPaths<T> & string> = P extends any
  ? DeepMakeOptional<T, P>
  : never;

export type MergeDeepObject<ObjA extends Literal, ObjB extends Literal> = {
  [key in Exclude<keyof ObjA, keyof ObjB>]: ObjA[key]
} & {
  [key in Exclude<keyof ObjB, keyof ObjA>]: ObjB[key]
} & {
  [key in Extract<keyof ObjA, keyof ObjB>]: ObjB[key] extends Literal
    ? MergeDeepObject<ObjA[key] extends Literal ? ObjA[key] : ObjB[key], ObjB[key]>
    : ObjB[key]
};

// Creates an union of object's values, including nested properties.
// type Thing = {
//   a: number;
//   b: {
//     c: string;
//     d: Date[]
//   }
// }
//
// DeepObjectValues<Thing> === number | string | Date[];

type DeepObjectValues<T> = T extends Nullable<Literal> ? {
  [key in keyof T]: T[key] extends Nullable<Literal> ? DeepObjectValues<T[key]> : T[key]
}[keyof T] : never;

export type ObjectValues<T> = T[keyof T];
export type GetPathValue<Obj, Path> = Path extends string
  ? Path extends `${infer Current}.${infer Rest}`
    ? Current extends keyof Obj
      ? GetPathValue<Obj[Current], Rest>
      : never
    : Path extends keyof Obj
      ? Obj[Path]
      : never
  : never;

type NestedPaths<T, Depth extends number> =
  T extends Nullable<Literal>
    ? `.${string & keyof ToPathObject<T, Decr[Depth]>}`
    : T extends Nullable<NonMutable<(infer U)[]>>
      ? `.[].${string & keyof ToPathObject<U, Decr[Depth]>}`
      : '';

export type ToPathObject<T, Depth extends number> =
  T extends Nullable<Literal>
    ? {
      [K in keyof T as
      K | `${K & string}${Depth extends 1
        ? ''
        : NestedPaths<T[K], Depth>
          }`
      ]: never
    }
    : never;

export type ObjectPaths<T, Depth extends number = 5> =
  T extends Literal
    ? keyof ToPathObject<Required<T>, Depth>
    : never;

type ToDeepOnlyPathObject<T> = Required<T> extends Literal ? {
  [K in keyof Required<T> as `${K & string}${Required<T>[K] extends Literal ? `.${string & keyof ToDeepOnlyPathObject<Required<T>[K]>}` : ''}`]: never
} : never;

export type DeepObjectPaths<T> = T extends Literal ? keyof ToDeepOnlyPathObject<T> : never;

export type FormPrefixes<T> = T extends Literal ? keyof ConditionalPick<{
  [key in keyof ToPathObject<T, 5>]-?: Get<T, key & string> extends Nullable<Literal | unknown[]>
    ? true : false;
}, true> : never;

export type NonMutable<T> = T extends (infer Item)[] ? (Item[] | readonly Item[]) : T;

export type ShallowReplaceNullable<T extends Literal> = {
  [key in keyof T]: Exclude<T[key], null | undefined>;
};

const merge = <T, U>(left: T, right: U): MergeObject<T, U> => ({
  ...left,
  ...right,
}) as MergeObject<T, U>

/**
 * Replaces values which match guard with values returned by replacer.
 * Used for replacing all Date objects with their formatted values (forms)
 *
 * @param obj object to map
 * @param guard function to determine which values to replace (e.g isDate, isNumber)
 * @param replacer function which replaces matched values
 */
export const replaceObjectValues = <Obj extends Literal, ToReplace, Replacement>(
  base: Obj,
  guard: (value: unknown) => value is ToReplace,
  replacer: (value: ToReplace, path: DeepObjectPaths<Obj>) => Replacement,
): ReplaceObjectValues<Obj, ToReplace, Replacement> => {
  const recurse = (obj: Literal, prefix = ''): ReplaceObjectValues<Obj, ToReplace, Replacement> => mapValues(obj, (value, key) => {
    const path = `${prefix}${key}`;

    if (guard(value)) {
      return replacer(value, path as DeepObjectPaths<Obj>);
    }

    if (isLiteral(value)) {
      return recurse(value, `${path}.`);
    }

    if (isArray(value)) {
      return value.map((element: unknown, i) => {
        const nodePath = `${path}.[${i}]`;

        if (isLiteral(element)) {
          return recurse(element, `${nodePath}.`);
        }

        return guard(element)
          ? replacer(element, nodePath as DeepObjectPaths<Obj>)
          : element;
      }) as Replacement[];
    }

    return value;
  }) as ReplaceObjectValues<Obj, ToReplace, Replacement>;

  return recurse(base);
};

const getValue = <T>(obj: T): GetValue<T> => (isLiteral(obj) && 'value' in obj
  ? getValue(obj.value)
  : obj) as GetValue<T>;

export function flattenValues<Obj extends Literal>(obj: Obj): FlattenValues<Obj>;
export function flattenValues<Obj extends Literal>(obj: Nullable<Obj>): Nullable<FlattenValues<Obj>>;
export function flattenValues<Obj extends Literal>(obj: Nullable<Obj>): Nullable<FlattenValues<Obj>> {
  if (isNil(obj)) {
    return null;
  }

  if ('value' in obj) {
    return getValue(obj) as FlattenValues<Obj>;
  }

  return mapValues(obj, (fieldValue) => {
    if (isLiteral(fieldValue)) {
      if ('value' in fieldValue) {
        return getValue(fieldValue);
      }

      return mapValues(fieldValue, (value) => (isLiteral(value)
        ? flattenValues(value)
        : value));
    }

    return fieldValue;
  }) as FlattenValues<Obj>;
}

type PickerFn<Obj> = (objValue: DeepObjectValues<Obj>, path: DeepObjectPaths<Obj>) => boolean;

export const pickDeep = <Obj extends Literal>(
  base: Obj,
  pickerFN: PickerFn<Obj>,
): PartialDeepObject<Obj> => {
  const recurse = (obj: Literal, prefix = '') => Object.entries(obj).reduce<Record<string, unknown>>((acc, [key, value]) => {
    const path = `${prefix}${key}`;

    if (isLiteral(value)) {
      // since PickerFn<Obj> already includes every possible leaf node type
      // this cast doesnt really matter
      const child = recurse(value, `${path}.`);
      if (!isEmpty(child)) {
        acc[key] = child;
      }

      // this one casts the "value" type to the type derrived from every value in this object
    } else if (pickerFN(value as DeepObjectValues<Obj>, path as DeepObjectPaths<Obj>)) {
      acc[key] = value;
    }

    return acc;
  }, {}) as PartialDeepObject<Obj>;

  return recurse(base);
};

export const compareObjectValues = <ObjA extends Literal, ObjB extends Literal>(
  left?: ObjA,
  right?: ObjB,
): Exclude<ObjectComparisonResult<MergeDeepObject<ObjA, ObjB>>, Change> => {
  const base = merge(left ?? {}, right ?? {});

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return mapValues<MergeDeepObject<ObjA, ObjB>, any>(base, (_: unknown, key: keyof ObjA & keyof ObjB) => {
    const previous = isLiteral(left) ? left[key] : left;
    const current = isLiteral(right) ? right[key] : right;

    if (isLiteral(previous) && isLiteral(current)) {
      return compareObjectValues(previous, current as ObjectShape<typeof previous>);
    }

    if (Array.isArray(previous) && Array.isArray(current)) {
      if (previous.length !== current.length) {
        return Change.unknown;
      }

      if (isArrayOf(previous, isLiteral) && isArrayOf(current, isLiteral)) {
        const compared = previous.map((item, index) => compareObjectValues(item, current[index]));

        return compared.every((item) => isEmpty(pickDeep(item as Literal, (value) => value !== Change.none)))
          ? Change.none
          : Change.unknown;
      }

      return previous.every(
        (item, index) => compareValues(castString(current[index]), castString(item)) === Change.none,
      ) ? Change.none : Change.unknown;
    }

    return compareValues<unknown>(castString(previous), castString(current));
  }) as unknown as Exclude<ObjectComparisonResult<MergeDeepObject<ObjA, ObjB>>, Change>;
};

export type GetDiffArgs<Left extends Literal, Right extends Literal> = Readonly<{
  left: Left;
  right: Right;
  omitFields?: (
    fieldName: DeepObjectPaths<Left> | DeepObjectPaths<Right>) => boolean;
}>;

export const getDiff = <Left extends Literal, Right extends Literal>({
  omitFields,
  left,
  right,
}: GetDiffArgs<Left, Right>): PartialDeepObject<ObjectComparisonResult<MergeDeepObject<Left, Right>>> => {
  const merged = compareObjectValues(left, right)
  const output = isLiteral(merged) ? pickDeep(
    merged,
    (change, path) => {
      if (change === Change.none) {
        return false;
      }

      if (isNil(omitFields)) {
        return true;
      }

      if (isString(path) && (isObjectDeepPath(left, path) || isObjectDeepPath(right, path))) {
        return !omitFields(path);
      }

      return true;
    },
  ) : {}

  return output as PartialDeepObject<ObjectComparisonResult<MergeDeepObject<Left, Right>>>;
};

export const fromEntries = <Keys extends string, Values>(
  arg: readonly (readonly [Keys, Values])[],
): Record<Keys, Values> => Object.fromEntries(arg) as Record<Keys, Values>;

export const toObjectEntries = <T extends Literal, U extends keyof T>(
  arg: T,
): string extends U ? [string, T[U]][] : [U, T[U]][] => Object.entries(
  arg
) as string extends U ? [string, T[U]][] : [U, T[U]][];

export const toObjectKeys = <T extends Literal, U extends keyof T>(
  arg: T
): string extends U ? string[] : U[] => Object.keys(arg) as string extends U ? string[] : U[];

export const isObjectKey = <T extends Literal>(
  arg: T, key: string | number): key is keyof T & (string | number) => key in arg;
